import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import {
  ArrowLeft, Gamepad2, Trophy, Clock, Calendar, Package,
  Tag, Globe, Loader2, Users, Building, Plus, CheckCircle2,
  ExternalLink, Copy, Shield, ShoppingCart, AlertTriangle, TrendingDown, Store, FileText, Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RichTextDisplay } from "@/components/ui/rich-text-editor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import UnplayableBadge from "@/components/UnplayableBadge";
import RelatedGames from "@/components/RelatedGames";
import { toast } from "sonner";
import { STORE_BY_VALUE } from "@/lib/storeOptions";

const API = `${getApiUrl()}/api`;

export default function BrowseGameInfo() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [adding, setAdding] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [addedGameId, setAddedGameId] = useState(null);
  const [existingInLibrary, setExistingInLibrary] = useState(null); // Track if already owned
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const [addAsCopy, setAddAsCopy] = useState(false); // Option to add as separate copy
  const [pcPrices, setPcPrices] = useState(null);
  const [pcPricesLoading, setPcPricesLoading] = useState(false);

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    fetchGame();
    fetchTags();
    checkIfInLibrary();
    checkWishlist();
  }, [gameId]);

  // Fetch PC prices from CheapShark when game loads
  useEffect(() => {
    if (!game) return;
    const hasPc = (game.platforms || []).some(p => p.toLowerCase() === "steam" || p.toLowerCase() === "pc");
    if (!hasPc || (game.delistedStatus && game.delistedStatus !== "none")) return;

    const fetchPcPrices = async () => {
      setPcPricesLoading(true);
      try {
        // Search for the game on CheapShark
        const searchRes = await axios.get(`${API}/prices/cheapshark/search`, { params: { title: game.name, limit: 5 } });
        const results = searchRes.data || [];
        if (results.length === 0) { setPcPrices(null); return; }

        // Find best match (exact or first result)
        const match = results.find(r => r.name.toLowerCase() === game.name.toLowerCase()) || results[0];

        // Get deals for matched game
        const dealsRes = await axios.get(`${API}/prices/cheapshark/deals/${match.gameID}`);
        setPcPrices(dealsRes.data);
      } catch {
        setPcPrices(null);
      } finally {
        setPcPricesLoading(false);
      }
    };
    fetchPcPrices();
  }, [game]);

  const checkIfInLibrary = async () => {
    try {
      const response = await axios.get(`${API}/games/check-master/${gameId}`);
      if (response.data.exists) {
        setExistingInLibrary(response.data.game);
      }
    } catch (err) {
      // Not in library or error - that's fine
      setExistingInLibrary(null);
    }
  };

  const checkWishlist = async () => {
    try {
      const res = await axios.get(`${API}/wishlist/check/${gameId}`);
      setInWishlist(!!res.data?.inWishlist);
    } catch {
      setInWishlist(false);
    }
  };

  const handleAddToWishlist = async () => {
    setWishlistBusy(true);
    try {
      await axios.post(`${API}/wishlist/${gameId}`);
      setInWishlist(true);
      toast.success("Added to your wish list");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to wish list");
    } finally {
      setWishlistBusy(false);
    }
  };

  const handleRemoveFromWishlist = async () => {
    setWishlistBusy(true);
    try {
      await axios.delete(`${API}/wishlist/${gameId}`);
      setInWishlist(false);
      toast.success("Removed from your wish list");
    } catch (err) {
      toast.error("Failed to remove from wish list");
    } finally {
      setWishlistBusy(false);
    }
  };

  const fetchGame = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/games/${gameId}`);
      setGame(response.data);
    } catch (err) {
      console.error("Failed to fetch game:", err);
      setError("Game not found or an error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/master/predefined-tags`);
      setPredefinedTags(response.data);
    } catch (err) {
      console.error("Failed to fetch tags:", err);
    }
  };

  const handleAddToLibrary = async (forceCopy = false) => {
    try {
      setAdding(true);
      const response = await axios.post(`${API}/games/add-from-master`, {
        masterGameId: gameId,
        addAsCopy: forceCopy || addAsCopy
      });
      setAddedGameId(response.data.id);
      setShowSuccessDialog(true);
      setExistingInLibrary(response.data); // Update to show it's now in library
      setInWishlist(false); // Adding to library auto-removes from wish list
      toast.success(forceCopy ? "Game added as another copy!" : "Game added to your library!");
    } catch (err) {
      console.error("Failed to add game:", err);
      if (err.response?.status === 409) {
        toast.error("This game is already in your library");
      } else {
        // Surface the backend's actual error so the user knows why and we
        // don't get stuck with the generic "Failed to add game to library".
        const detail =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "Failed to add game to library";
        toast.error(typeof detail === "string" ? detail : "Failed to add game to library");
      }
    } finally {
      setAdding(false);
    }
  };

  const getTagInfo = (tagId) => {
    return predefinedTags.find(t => t.id === tagId);
  };

  // Get genre tags from achievements
  const getGenreTags = () => {
    if (!game?.achievements || !predefinedTags.length) return [];
    
    const tagCounts = {};
    game.achievements.forEach(ach => {
      (ach.tags || []).forEach(tagId => {
        const tag = getTagInfo(tagId);
        if (tag && tag.category === "genre") {
          tagCounts[tagId] = (tagCounts[tagId] || 0) + 1;
        }
      });
    });
    
    // Sort by count and return top tags
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tagId]) => getTagInfo(tagId))
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-[#050505] p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="text-center py-12">
            <Gamepad2 className="w-16 h-16 mx-auto mb-4 text-white/20" />
            <p className="text-white/60">{error || "Game not found"}</p>
          </div>
        </div>
      </div>
    );
  }

  const genreTags = getGenreTags();

  // Wish list is disabled when the game can no longer be obtained/played:
  //  - digital-only AND delisted (the only way to buy it is gone), or
  //  - flagged currently unplayable.
  const digitalOnly = !!game.availableDigital && !game.availablePhysical;
  const isDelisted = !!game.delistedStatus && game.delistedStatus !== "none";
  const wishlistDisabledReason = game.isUnplayable
    ? "This game is currently marked as unplayable, so it can't be added to your wish list."
    : (digitalOnly && isDelisted)
    ? "This game is digital-only and has been delisted, so it can no longer be purchased."
    : null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#121212] to-[#050505] border-b border-white/5">
        <div className="max-w-4xl mx-auto p-4 sm:p-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Browse
          </Button>
          
          {/* Mobile-first layout */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            {/* Box Art - Centered on mobile */}
            <div className="flex justify-center sm:justify-start sm:flex-shrink-0">
              <div className="w-32 sm:w-40 aspect-[2/3] rounded-lg overflow-hidden bg-[#121212] shadow-xl">
                {game.boxArtUrl ? (
                  <img
                    src={game.boxArtUrl}
                    alt={game.name}
                    className="w-full h-full object-contain bg-black/50"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gamepad2 className="w-10 h-10 text-white/20" />
                  </div>
                )}
              </div>
            </div>
            
            {/* Game Info */}
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3" data-testid="game-title">
                {game.name}
              </h1>

              {game.isUnplayable && (
                <div className="flex justify-center sm:justify-start mb-3">
                  <UnplayableBadge />
                </div>
              )}
              
              {/* Platforms & Systems - Centered on mobile */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mb-4">
                {game.platforms?.map(p => (
                  <PlatformBadge key={p} platform={p} size="sm" />
                ))}
                {game.systems?.map(s => (
                  <SystemBadge key={s} system={s} size="sm" />
                ))}
                {game.region && game.region !== "none" && game.region !== "WW" && (
                  <span className="text-xs px-2 py-1 rounded bg-gray-500/20 text-gray-300 border border-gray-500/30">
                    <Globe className="w-3 h-3 inline mr-1" />
                    {game.region}
                  </span>
                )}
              </div>

              {/* Add to Library Button - with library status */}
              {existingInLibrary ? (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <div className="flex-1">
                      <p className="text-green-400 font-medium text-sm">Already in your library</p>
                      <p className="text-white/50 text-xs">Added as "{existingInLibrary.name || game?.name}"</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/games/${existingInLibrary.id}`)}
                      className="text-green-400 hover:text-green-300"
                    >
                      View
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                  <Button
                    onClick={() => handleAddToLibrary(true)}
                    disabled={adding}
                    variant="outline"
                    className="w-full sm:w-auto border-white/20 text-white/70 hover:text-white"
                    data-testid="add-another-copy-btn"
                  >
                    {adding ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Add Another Copy
                  </Button>
                </div>
              ) : (
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() => handleAddToLibrary(false)}
                      disabled={adding}
                      className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
                      data-testid="add-to-library-btn"
                    >
                      {adding ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Add to Library
                    </Button>
                    {inWishlist ? (
                      <Button
                        onClick={handleRemoveFromWishlist}
                        disabled={wishlistBusy}
                        variant="outline"
                        className="w-full sm:w-auto border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                        data-testid="remove-from-wishlist-btn"
                      >
                        {wishlistBusy ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                        )}
                        In Wish List
                      </Button>
                    ) : wishlistDisabledReason ? (
                      <Button
                        disabled
                        variant="outline"
                        className="w-full sm:w-auto border-white/10 text-white/40 cursor-not-allowed opacity-60"
                        data-testid="add-to-wishlist-btn"
                        title={wishlistDisabledReason}
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        Add to Wish List
                      </Button>
                    ) : (
                      <Button
                        onClick={handleAddToWishlist}
                        disabled={wishlistBusy}
                        variant="outline"
                        className="w-full sm:w-auto border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                        data-testid="add-to-wishlist-btn"
                      >
                        {wishlistBusy ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Gift className="w-4 h-4 mr-2" />
                        )}
                        Add to Wish List
                      </Button>
                    )}
                  </div>
                  {wishlistDisabledReason && !inWishlist && (
                    <p
                      className="text-amber-400/80 text-xs mt-2 flex items-start gap-1.5"
                      data-testid="wishlist-disabled-note"
                    >
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      {wishlistDisabledReason}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Achievement Slop notice — REPLACES the achievements/gamerscore/
            completion stats card. The whole point of the flag is that these
            achievements don't count, so surfacing the counts on the public
            detail page would be misleading. */}
        {game.isAchievementSlop ? (
          <Card className="bg-amber-500/5 border-amber-500/40" data-testid="browse-detail-slop-card">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-amber-300 font-semibold text-base mb-1">
                  Achievement Slop
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  This game exists solely to increase achievement/trophy counts
                  and/or provide score boosts. Game includes numerous (over
                  500) easy to unlock (takes less than an hour) achievements.
                  Achievement data is not to be stored on this platform, and
                  notes features and achievement tracking are disabled.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Stats Card */
          <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-[#121212] rounded-lg">
                <Trophy className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                <p className="text-lg sm:text-xl font-bold text-white">{game.totalAchievements || 0}</p>
                <p className="text-xs text-white/40">Achievements</p>
              </div>
              <div className="text-center p-3 bg-[#121212] rounded-lg">
                <span className="text-yellow-400 text-lg font-bold block mb-1">G</span>
                <p className="text-lg sm:text-xl font-bold text-white">{(game.totalScore || 0).toLocaleString()}</p>
                <p className="text-xs text-white/40">Gamerscore</p>
              </div>
              {game.dlcs?.length > 0 && (
                <div className="text-center p-3 bg-[#121212] rounded-lg">
                  <Package className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                  <p className="text-lg sm:text-xl font-bold text-white">{game.dlcs.length}</p>
                  <p className="text-xs text-white/40">DLC Packs</p>
                </div>
              )}
              {game.completionEstimate && (
                <div className="text-center p-3 bg-[#121212] rounded-lg">
                  <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-sm sm:text-base font-bold text-white">{game.completionEstimate}</p>
                  <p className="text-xs text-white/40">Est. Completion</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Game Details Card */}
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="p-4 space-y-4">
            <h2 className="text-white font-semibold text-lg">Game Details</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {((game.developers?.length > 0) || game.developer) && (
                <div className="flex items-start gap-3 p-3 bg-[#121212] rounded-lg">
                  <Users className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Developer</p>
                    <p className="text-white text-sm">{game.developers?.length > 0 ? game.developers.join(", ") : game.developer}</p>
                  </div>
                </div>
              )}
              {((game.publishers?.length > 0) || game.publisher) && (
                <div className="flex items-start gap-3 p-3 bg-[#121212] rounded-lg">
                  <Building className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Publisher</p>
                    <p className="text-white text-sm">{game.publishers?.length > 0 ? game.publishers.join(", ") : game.publisher}</p>
                  </div>
                </div>
              )}
              {game.releaseDate && (
                <div className="flex items-start gap-3 p-3 bg-[#121212] rounded-lg">
                  <Calendar className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Release Date</p>
                    <p className="text-white text-sm">{game.releaseDate}</p>
                  </div>
                </div>
              )}
              {game.region && game.region !== "none" && (
                <div className="flex items-start gap-3 p-3 bg-[#121212] rounded-lg">
                  <Globe className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Region</p>
                    <p className="text-white text-sm">{game.region === "WW" ? "Worldwide" : game.region}</p>
                  </div>
                </div>
              )}
              {game.ratings?.length > 0 && (
                <div className="flex items-start gap-3 p-3 bg-[#121212] rounded-lg" data-testid="browse-game-ratings">
                  <Shield className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Rating</p>
                    <p className="text-white text-sm">{game.ratings.join(", ")}</p>
                  </div>
                </div>
              )}
              {(game.completionEstimateFast || game.completionEstimateLong) && (
                <div className="flex items-start gap-3 p-3 bg-[#121212] rounded-lg" data-testid="browse-game-completion-range">
                  <Clock className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Completion Estimate</p>
                    <p className="text-white text-sm">
                      {game.completionEstimateFast && game.completionEstimateLong
                        ? `${game.completionEstimateFast} - ${game.completionEstimateLong}`
                        : game.completionEstimateFast || game.completionEstimateLong}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Related games (alternate versions, remasters, remakes, sequels,
            prequels, spin-offs, collections) — placed below Game Details since
            this page is about the original game, not the related ones. */}
        <RelatedGames gameId={gameId} />

        {/* Custom Notes from Master Database */}
        {game.customNotes && (
          <Card className="bg-[#0A0A0A] border-cyan-500/10" data-testid="browse-custom-notes">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-cyan-400/70" />
                <h2 className="text-white font-semibold">Game Notes</h2>
              </div>
              <RichTextDisplay content={game.customNotes} />
            </CardContent>
          </Card>
        )}

        {/* Genre Tags */}
        {genreTags.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-white/40" />
                <h2 className="text-white font-semibold">Genres</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {genreTags.map(tag => (
                  <span
                    key={tag.id}
                    className="text-xs px-3 py-1.5 rounded-full"
                    style={{ backgroundColor: tag.color + "20", color: tag.color, border: `1px solid ${tag.color}40` }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Buy on Amazon — or Delisted Notice — or Digital Only */}
        {game.delistedStatus && game.delistedStatus !== "none" ? (
          <Card className="bg-[#0A0A0A] border-amber-500/20" data-testid="delisted-notice-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h2 className="text-amber-400 font-semibold text-sm">
                  {game.delistedStatus === "servers-shutdown" && "Servers Shut Down"}
                  {game.delistedStatus === "removed-digital" && "Removed from Digital Stores"}
                  {game.delistedStatus === "no-longer-playable" && "No Longer Playable"}
                  {game.delistedStatus === "delisted" && "Delisted"}
                  {game.delistedStatus === "other" && "Unavailable"}
                </h2>
              </div>
              {game.delistedReason && (
                <p className="text-white/50 text-xs leading-relaxed">{game.delistedReason}</p>
              )}
              {!game.delistedReason && (
                <p className="text-white/30 text-xs">This game is no longer available for purchase.</p>
              )}
            </CardContent>
          </Card>
        ) : game.availableDigital && !game.availablePhysical ? (
          <Card className="bg-[#0A0A0A] border-blue-500/20" data-testid="digital-only-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-400" />
                <h2 className="text-blue-400 font-semibold text-sm">Digital Only</h2>
              </div>
              <p className="text-white/40 text-xs mt-1">This game is only available as a digital download.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#0A0A0A] border-white/5" data-testid="amazon-affiliate-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ShoppingCart className="w-4 h-4 text-[#FF9900]" />
                <h2 className="text-white font-semibold">Buy This Game</h2>
              </div>
              <div className="space-y-2">
                {(() => {
                  const AMAZON_TAG = "gameorang-20";
                  const gameName = encodeURIComponent(game.name);
                  const platformButtons = [];

                  // Build platform-specific search terms
                  const platforms = game.platforms || (game.platform ? [game.platform] : []);
                  const platformSearchTerms = {
                    xbox: "Xbox",
                    playstation: "PlayStation",
                    steam: "PC",
                  };

                  if (platforms.length > 0) {
                    platforms.forEach(p => {
                      const label = platformSearchTerms[p.toLowerCase()] || p;
                      const searchQuery = encodeURIComponent(`${game.name} ${label}`);
                      platformButtons.push(
                        <a
                          key={p}
                          href={`https://www.amazon.com/s?k=${searchQuery}&tag=${AMAZON_TAG}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full p-3 bg-[#121212] hover:bg-[#FF9900]/10 border border-white/5 hover:border-[#FF9900]/30 rounded-lg transition-all group"
                          data-testid={`amazon-buy-${p}`}
                        >
                          <ShoppingCart className="w-4 h-4 text-[#FF9900] group-hover:scale-110 transition-transform" />
                          <span className="text-white group-hover:text-[#FF9900] text-sm font-medium transition-colors">
                            Buy for {label} on Amazon
                          </span>
                          <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-[#FF9900]/60 ml-auto" />
                        </a>
                      );
                    });
                  } else {
                    // No platform info — generic search
                    platformButtons.push(
                      <a
                        key="generic"
                        href={`https://www.amazon.com/s?k=${gameName}&tag=${AMAZON_TAG}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full p-3 bg-[#121212] hover:bg-[#FF9900]/10 border border-white/5 hover:border-[#FF9900]/30 rounded-lg transition-all group"
                        data-testid="amazon-buy-generic"
                      >
                        <ShoppingCart className="w-4 h-4 text-[#FF9900] group-hover:scale-110 transition-transform" />
                        <span className="text-white group-hover:text-[#FF9900] text-sm font-medium transition-colors">
                          Search on Amazon
                        </span>
                        <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-[#FF9900]/60 ml-auto" />
                      </a>
                    );
                  }

                  return platformButtons;
                })()}
              </div>
              <p className="text-white/20 text-[10px] mt-3 text-center">
                Affiliate link — supports Game-o-Rang at no extra cost to you
              </p>
            </CardContent>
          </Card>
        )}

        {/* PC Price Comparison (CheapShark) */}
        {pcPricesLoading && (
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardContent className="p-4 flex items-center gap-2 justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-teal-400" />
              <span className="text-white/40 text-sm">Loading PC prices...</span>
            </CardContent>
          </Card>
        )}
        {pcPrices && pcPrices.deals?.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/5" data-testid="pc-price-comparison-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-teal-400" />
                  <h2 className="text-white font-semibold">PC Prices</h2>
                </div>
                {pcPrices.cheapestEver?.price && pcPrices.cheapestEver.price !== "N/A" && (
                  <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full" data-testid="cheapest-ever-badge">
                    <TrendingDown className="w-3 h-3" />
                    All-time low: ${pcPrices.cheapestEver.price}
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {pcPrices.deals.slice(0, 6).map((deal) => (
                  <a
                    key={deal.dealID}
                    href={`https://www.cheapshark.com/redirect?dealID=${deal.dealID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2.5 bg-[#121212] hover:bg-teal-500/5 border border-white/5 hover:border-teal-500/20 rounded-lg transition-all group"
                    data-testid={`pc-deal-${deal.storeID}`}
                  >
                    <img
                      src={deal.storeIcon}
                      alt={deal.storeName}
                      className="w-5 h-5 object-contain"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <span className="text-white/80 text-sm flex-1 group-hover:text-white transition-colors">
                      {deal.storeName}
                    </span>
                    {deal.savings > 0 && (
                      <span className="text-emerald-400 text-xs font-medium bg-emerald-400/10 px-1.5 py-0.5 rounded">
                        -{deal.savings}%
                      </span>
                    )}
                    <span className={`text-sm font-semibold ${deal.savings > 0 ? "text-emerald-400" : "text-white"}`}>
                      ${deal.price}
                    </span>
                    {deal.savings > 0 && (
                      <span className="text-white/25 text-xs line-through">${deal.retailPrice}</span>
                    )}
                    <ExternalLink className="w-3 h-3 text-white/20 group-hover:text-teal-400/60" />
                  </a>
                ))}
              </div>
              <p className="text-white/20 text-[10px] mt-3 text-center">
                Prices via CheapShark — updated in real time
              </p>
            </CardContent>
          </Card>
        )}

        {/* DLC Information */}
        {game.dlcs?.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-purple-400" />
                <h2 className="text-white font-semibold">DLC Content</h2>
              </div>
              <div className="space-y-2">
                {game.dlcs.map(dlc => {
                  const dlcAchievements = game.achievements?.filter(a => a.dlcId === dlc.id) || [];
                  const dlcScore = dlcAchievements.reduce((sum, a) => sum + (a.score || 0), 0);
                  
                  return (
                    <div key={dlc.id} className="flex items-center gap-3 p-3 bg-[#121212] rounded-lg">
                      {dlc.boxArtUrl ? (
                        <img
                          src={dlc.boxArtUrl}
                          alt={dlc.name}
                          className="w-10 h-10 rounded object-contain bg-black/50 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                          <Package className="w-5 h-5 text-purple-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{dlc.name}</p>
                        <p className="text-xs text-white/40">
                          {dlcAchievements.length} achievements • {dlcScore.toLocaleString()}G
                          {dlc.completionEstimate && ` • ${dlc.completionEstimate}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bottom Add to Library Button */}
        <div className="pt-4">
          {existingInLibrary ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Already in your library</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/games/${existingInLibrary.id}`)}
                  className="text-green-400 hover:text-green-300"
                >
                  View Game
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <Button
                onClick={() => handleAddToLibrary(true)}
                disabled={adding}
                variant="outline"
                className="w-full border-white/20 text-white/70 hover:text-white"
              >
                {adding ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Add Another Copy
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => handleAddToLibrary(false)}
              disabled={adding}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="add-to-library-btn-bottom"
            >
              {adding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add to My Library
            </Button>
          )}
        </div>

        {/* Custom per-game external links (admin-managed in MDB).
            Positioned at the bottom of the page below the library status
            so the buy/find options sit alongside the library actions. */}
        {Array.isArray(game.links) && game.links.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/5" data-testid="game-links-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ExternalLink className="w-4 h-4 text-cyan-400" />
                <h2 className="text-white font-semibold">More Links</h2>
              </div>
              <div className="space-y-2">
                {[...game.links]
                  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                  .map((link, idx) => {
                    if (!link?.url) return null;
                    const store = STORE_BY_VALUE[link.storeName] || STORE_BY_VALUE.other;
                    return (
                      <a
                        key={link.id || idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 w-full p-3 bg-[#121212] hover:bg-white/5 border border-white/5 rounded-lg transition-all group"
                        style={{ borderColor: store.color + "30" }}
                        data-testid={`game-link-${idx}`}
                      >
                        {link.thumbnailUrl ? (
                          <img
                            src={link.thumbnailUrl}
                            alt=""
                            className="w-10 h-10 object-cover rounded flex-shrink-0 bg-black/30"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <div
                            className="w-10 h-10 flex items-center justify-center rounded flex-shrink-0"
                            style={{ backgroundColor: store.color + "20" }}
                          >
                            <Store className="w-5 h-5" style={{ color: store.color }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="text-[9px] px-1 py-0 rounded font-semibold uppercase tracking-wide flex-shrink-0"
                              style={{ backgroundColor: store.color + "30", color: store.color }}
                            >
                              {store.label}
                            </span>
                            <span className="text-white text-sm font-medium truncate group-hover:text-cyan-300 transition-colors">
                              {link.heading || link.url}
                            </span>
                          </div>
                          {link.description && (
                            <p className="text-white/40 text-[11px] truncate mt-0.5">{link.description}</p>
                          )}
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-white/30 group-hover:text-cyan-300/80" />
                      </a>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="w-5 h-5" />
              Game Added!
            </DialogTitle>
            <DialogDescription className="text-white/60">
              <span className="text-white font-medium">{game?.name}</span> has been added to your library.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 pt-4">
            <Button
              onClick={() => window.location.href = `/games/${addedGameId}`}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="go-to-game-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Go to Game Page
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowSuccessDialog(false);
                navigate("/browse");
              }}
              className="w-full border-white/20 text-white hover:bg-white/10"
              data-testid="continue-browsing-btn"
            >
              Continue Browsing
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
